import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ACTIONS, COMM_SOURCE_LABEL } from "@/lib/api";
import { StatusPill } from "@/components/Pills";
import { ArrowLeft, Check, FileSignature, Layers, Users, Wallet, Network, Sparkles, Hammer, Gavel, AlertTriangle, ScrollText, Library, Brain, Recycle, MessagesSquare } from "lucide-react";

const TABS = [
  { id: "intent", label: "Code Intent", icon: <ScrollText size={14} /> },
  { id: "custody", label: "Custody", icon: <Library size={14} /> },
  { id: "ai_slm", label: "AI / SLM Readiness", icon: <Brain size={14} /> },
  { id: "scope", label: "Scope", icon: <Layers size={14} /> },
  { id: "ownership", label: "Ownership", icon: <Users size={14} /> },
  { id: "budget", label: "Budget", icon: <Wallet size={14} /> },
  { id: "dependencies", label: "Dependencies", icon: <Network size={14} /> },
  { id: "advisory", label: "AI Advisory Plan", icon: <Sparkles size={14} /> },
  { id: "evidence", label: "dtool Evidence", icon: <Hammer size={14} /> },
  { id: "release", label: "Release Gate", icon: <Gavel size={14} /> },
  { id: "receipt", label: "KATLAS Receipt", icon: <FileSignature size={14} /> },
];

const REUSE_LABEL = {
  new_build: "New build",
  refactor: "Refactor",
  reusable_pattern: "Reusable pattern",
};

const EVIDENCE_SOURCE_LABEL = {
  dtool_build: "dtool build",
  devtool_job_record: "devtool job record",
  test_runner: "test runner",
  cto_review: "CTO review",
  security_review: "security review",
  deployment_checklist: "deployment checklist",
  rollback_log: "rollback log",
};

function Section({ title, eyebrow, children, testId }) {
  return (
    <div className="bc-card p-7" data-testid={testId}>
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      {title && <h3 className="font-display text-[22px] mb-4 tracking-tight">{title}</h3>}
      {children}
    </div>
  );
}

function PillList({ items, tone }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((it, i) => (
        <span key={i} className="bc-status" data-tone={tone || "default"}>{it}</span>
      ))}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [advisory, setAdvisory] = useState(null);
  const [tab, setTab] = useState("intent");
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState({ action: "developer_review_completed", actor_role: "CTO", note: "" });
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get(`/requests/${id}`);
    setData(r.data);
    const a = await api.get(`/advisory/${id}`);
    setAdvisory(a.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const req = data?.request;
  const actions = data?.actions || [];
  const receipt = data?.receipt;

  const onRecord = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      await api.post(`/requests/${id}/actions`, actionState);
      await load();
    } finally { setPosting(false); }
  };

  const counts = useMemo(() => ({
    deps: req?.dependencies?.length || 0,
    risks: req?.risks?.length || 0,
    evidence: req?.evidence_required?.length || 0,
    owners: Object.keys(req?.owner_roles || {}).length,
  }), [req]);

  if (loading || !req) {
    return <div className="text-sm text-[hsl(var(--bc-muted))]">Loading…</div>;
  }

  return (
    <div data-testid="request-detail-page">
      <Link to="/requests" className="bc-btn-text inline-flex items-center gap-2" data-testid="back-to-requests">
        <ArrowLeft size={14} /> Back to requests
      </Link>

      <header className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start bc-rise">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="font-mono text-sm text-[hsl(var(--bc-muted))]">{req.request_id}</span>
            <StatusPill status={req.status} />
            {req.reuse_check?.type && (
              <span
                className="bc-status"
                data-tone={req.reuse_check.type === "reusable_pattern" ? "success" : req.reuse_check.type === "refactor" ? "signal" : "default"}
                data-testid={`reuse-badge-${req.reuse_check.type}`}
                title={req.reuse_check.note}
              >
                <Recycle size={11} /> Reuse check · {REUSE_LABEL[req.reuse_check.type] || req.reuse_check.type}
              </span>
            )}
          </div>
          <h1 className="font-display text-[38px] sm:text-[46px] tracking-tight leading-[1.05]" data-testid="request-title">
            {req.requested_module}
          </h1>
          <p className="mt-4 max-w-3xl text-[15px] leading-[1.65] text-[hsl(var(--bc-ink-soft))]">
            Sponsor: <span className="text-[hsl(var(--bc-ink))]">{req.sponsor_role}</span> · {req.business_purpose}
          </p>
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 gap-3">
          <Stat k="Owners" v={counts.owners} />
          <Stat k="Dependencies" v={counts.deps} />
          <Stat k="Risks" v={counts.risks} />
          <Stat k="Evidence items" v={counts.evidence} />
        </div>
      </header>

      <nav className="mt-10 border-b border-[hsl(var(--border))] flex gap-1 overflow-x-auto bc-no-scrollbar" data-testid="detail-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="bc-tab flex items-center gap-2"
            data-active={tab === t.id}
            data-testid={`tab-${t.id}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </nav>

      {req.out_of_band_decision && <OutOfBandPanel oob={req.out_of_band_decision} />}

      <section className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {tab === "intent" && (
            <>
              <Section testId="panel-intent" eyebrow="Code Intent Pack" title="Sponsor lens — seven questions answered">
                <p className="text-xs text-[hsl(var(--bc-muted))] mb-5">
                  A sponsor-readable framing of the change. AI may suggest within the advisory boundary;
                  human authority sets and signs the rest.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <IntentCell k="Intended change" v={req.code_intent_pack?.intended_change} />
                  <IntentCell k="Business purpose" v={req.code_intent_pack?.business_purpose} />
                  <IntentCell k="Affected system area" v={req.code_intent_pack?.affected_system_area} />
                  <IntentCell k="Human / CTO authority" v={req.code_intent_pack?.human_authority_boundary} />
                  <IntentCell k="AI advisory boundary" v={req.code_intent_pack?.ai_advisory_boundary} tone="signal" />
                  <IntentCell k="KATLAS receipt event" v={req.code_intent_pack?.katlas_receipt_event} mono />
                </div>
                <div className="rule my-6" />
                <p className="eyebrow mb-3">Behaviour that must remain unchanged</p>
                <ul className="space-y-2 text-sm">
                  {(req.code_intent_pack?.behaviour_unchanged || []).map((b, i) => (
                    <li key={i} className="flex gap-2"><Check size={14} className="text-[hsl(var(--bc-signal))] mt-1"/>{b}</li>
                  ))}
                </ul>
                <div className="rule my-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="eyebrow mb-3">Evidence required</p>
                    <ul className="space-y-2 text-sm">
                      {(req.code_intent_pack?.evidence_required || []).map((e, i) => <li key={i} className="flex gap-2"><span className="signal-dot mt-2"/>{e}</li>)}
                    </ul>
                  </div>
                  <div>
                    <p className="eyebrow mb-3">Release evidence required</p>
                    <ul className="space-y-2 text-sm">
                      {(req.code_intent_pack?.release_evidence_required || []).map((e, i) => <li key={i} className="flex gap-2"><span className="signal-dot mt-2"/>{e}</li>)}
                    </ul>
                  </div>
                </div>
              </Section>
              {req.reuse_check?.type && (
                <Section testId="panel-reuse" eyebrow="Reuse check" title="Is this a new build, a refactor, or a reusable pattern?">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bc-status" data-tone={req.reuse_check.type === "reusable_pattern" ? "success" : req.reuse_check.type === "refactor" ? "signal" : "default"}>
                      {REUSE_LABEL[req.reuse_check.type]}
                    </span>
                  </div>
                  <p className="text-[15px] text-[hsl(var(--bc-ink-soft))]">{req.reuse_check.note}</p>
                </Section>
              )}
            </>
          )}

          {tab === "custody" && (
            <Section testId="panel-custody" eyebrow="Custody of build knowledge" title="Who controls each artefact">
              <p className="text-xs text-[hsl(var(--bc-muted))] mb-5">
                Custody stays at source. Raw source code, secrets, raw logs and raw prompts are never travelled or exposed.
              </p>
              <div className="divide-y divide-[hsl(var(--border))]">
                {Object.entries(req.custody_of_build_knowledge || {}).map(([k, v]) => (
                  <div key={k} className="py-4 grid grid-cols-3 gap-4" data-testid={`custody-row-${k}`}>
                    <div className="text-[14px] font-medium">{k.replace(/_/g, " ")}</div>
                    <div className="col-span-2 text-sm text-[hsl(var(--bc-ink-soft))]">{v}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {tab === "ai_slm" && (
            <Section testId="panel-ai-slm" eyebrow="AI / SLM readiness" title="Context governance only — not a live model">
              <div className="bc-status mb-4" data-tone="signal" data-testid="ai-slm-label">{req.ai_slm_readiness?.label}</div>
              <p className="text-sm text-[hsl(var(--bc-muted))] mb-5">
                This is not a live AI model and not live SLM training. It indicates whether the change request
                has the curated context a future AI-assisted refactor would need — and whether the boundary
                around AI usage is defined.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(req.ai_slm_readiness || {})
                  .filter(([k]) => k !== "label")
                  .map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border border-[hsl(var(--border))] rounded p-3 text-sm">
                      <span>{k.replace(/_/g, " ")}</span>
                      <span className="bc-status" data-tone={v ? "success" : "warn"} data-testid={`ai-slm-${k}`}>{v ? "ready" : "not yet"}</span>
                    </div>
                  ))}
              </div>
              <div className="rule my-6" />
              <p className="text-sm text-[hsl(var(--bc-signal))] font-medium">
                AI may suggest within the advisory boundary. AI must not approve, merge, deploy, fabricate
                evidence or judge developer performance.
              </p>
            </Section>
          )}

          {tab === "scope" && (
            <>
              <Section testId="panel-scope" eyebrow="Scope" title="Proposed changes">
                <ul className="space-y-3">
                  {req.proposed_changes.map((c, i) => (
                    <li key={i} className="flex gap-3 text-[15px]"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1 shrink-0" />{c}</li>
                  ))}
                </ul>
              </Section>
              <Section testId="panel-current" eyebrow="What already exists" title="Current system references">
                <PillList items={req.current_system_refs} />
              </Section>
            </>
          )}

          {tab === "ownership" && (
            <Section testId="panel-ownership" eyebrow="Ownership" title="Who owns what">
              <div className="divide-y divide-[hsl(var(--border))]">
                {Object.entries(req.owner_roles).map(([role, scope]) => (
                  <div key={role} className="py-4 grid grid-cols-3 gap-4">
                    <div className="text-[15px] font-medium">{role}</div>
                    <div className="col-span-2 text-sm text-[hsl(var(--bc-ink-soft))]">{scope}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {tab === "budget" && (
            <Section testId="panel-budget" eyebrow="Budget" title="Effort & budget bands">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="eyebrow mb-2">Effort band</p>
                  <p className="font-display text-[28px]">{req.estimated_effort_band}</p>
                </div>
                <div>
                  <p className="eyebrow mb-2">Budget band</p>
                  <p className="font-display text-[28px]">{req.estimated_budget_band}</p>
                </div>
              </div>
              <div className="rule my-6" />
              <p className="text-sm text-[hsl(var(--bc-muted))]">
                Bands are advisory and synthetic. Approval requires the named authority below.
              </p>
              <div className="mt-4">
                <p className="eyebrow mb-2">Authority required</p>
                <PillList items={req.authority_required} tone="signal" />
              </div>
            </Section>
          )}

          {tab === "dependencies" && (
            <>
              <Section testId="panel-deps" eyebrow="Dependencies" title="Upstream / downstream">
                <PillList items={req.dependencies} />
              </Section>
              <Section testId="panel-risks" eyebrow="Risks" title="Tracked risks">
                <ul className="space-y-3">
                  {req.risks.map((r, i) => (
                    <li key={i} className="flex gap-3 text-[15px]"><AlertTriangle size={16} className="text-[hsl(var(--bc-signal))] mt-1 shrink-0" />{r}</li>
                  ))}
                </ul>
              </Section>
            </>
          )}

          {tab === "advisory" && advisory && (
            <Section testId="panel-advisory" eyebrow="AI advisory plan" title="Deterministic suggestions">
              <div className="bc-status mb-4" data-tone="signal">{advisory.label}</div>
              <p className="text-[15px] leading-[1.6]">{advisory.sponsor_summary}</p>
              <div className="rule my-6" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="eyebrow mb-3">Suggested steps</p>
                  <ul className="space-y-2 text-sm">
                    {advisory.suggested_steps.map((s, i) => <li key={i} className="flex gap-2"><span className="signal-dot mt-2"/>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">Developer checklist</p>
                  <ul className="space-y-2 text-sm">
                    {advisory.developer_checklist.map((s, i) => <li key={i} className="flex gap-2"><Check size={14} className="text-[hsl(var(--bc-signal))] mt-1"/>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">Missing evidence prompts</p>
                  <ul className="space-y-2 text-sm">
                    {advisory.missing_evidence_prompts.length === 0 && <li className="text-[hsl(var(--bc-muted))]">All evidence prompts captured.</li>}
                    {advisory.missing_evidence_prompts.map((s, i) => <li key={i} className="flex gap-2"><span className="signal-dot mt-2"/>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">Release readiness questions</p>
                  <ul className="space-y-2 text-sm">
                    {advisory.release_readiness_questions.map((s, i) => <li key={i} className="flex gap-2"><span className="signal-dot mt-2"/>{s}</li>)}
                  </ul>
                </div>
              </div>
              <div className="rule my-6" />
              <p className="text-sm text-[hsl(var(--bc-signal))] font-medium">{advisory.ai_safety_line}</p>
            </Section>
          )}

          {tab === "evidence" && (
            <Section testId="panel-evidence" eyebrow="dtool evidence" title="Build / test / deploy hashes by source">
              <p className="text-sm text-[hsl(var(--bc-muted))] mb-5">
                Evidence stays at source. Only hashes travel to BuildCierge. Raw logs, prompts and source code are never exposed.
              </p>
              <div className="space-y-2">
                {(req.evidence_items || req.evidence_required?.map((e) => ({ label: e, source_type: "devtool_job_record" })) || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between border border-[hsl(var(--border))] rounded p-3 gap-3" data-testid={`evidence-row-${i}`}>
                    <div className="text-sm flex-1">{e.label}</div>
                    <span className="bc-status" data-tone="signal" data-testid={`evidence-source-${e.source_type}`}>
                      {EVIDENCE_SOURCE_LABEL[e.source_type] || e.source_type}
                    </span>
                    <div className="font-mono text-xs text-[hsl(var(--bc-muted))]">awaiting hash</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {tab === "release" && (
            <Section testId="panel-release" eyebrow="Release readiness gate" title="Gate checklist">
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1"/>Authority chain signed in correct order</li>
                <li className="flex gap-3"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1"/>Every evidence_required item produced a hash</li>
                <li className="flex gap-3"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1"/>Environment drift checked vs. production manifest</li>
                <li className="flex gap-3"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1"/>Rollback path documented and tested</li>
                <li className="flex gap-3"><Check size={16} className="text-[hsl(var(--bc-signal))] mt-1"/>KATLAS delivery receipt signed</li>
              </ul>
            </Section>
          )}

          {tab === "receipt" && (
            <Section testId="panel-receipt" eyebrow="KATLAS receipt" title={receipt ? (receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference ? "KATLAS-signed delivery receipt" : "Pending — not KATLAS-signed") : "No receipt recorded yet"}>
              {receipt ? (
                <>
                  <div
                    className={`mb-6 flex items-start gap-3 p-4 rounded border ${receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference ? "border-[hsl(var(--bc-success)/0.4)] bg-[hsl(var(--bc-success)/0.06)]" : "border-[hsl(var(--bc-signal)/0.4)] bg-[hsl(var(--bc-signal)/0.06)]"}`}
                    data-testid={receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference ? "receipt-banner-signed" : "receipt-banner-pending"}
                  >
                    <span className="signal-dot mt-2" style={{ background: receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference ? "hsl(var(--bc-success))" : "hsl(var(--bc-signal))" }} />
                    <div>
                      <p className="font-medium text-sm">
                        {receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference
                          ? "KATLAS-signed"
                          : "Pending — not KATLAS-signed"}
                      </p>
                      <p className="text-xs text-[hsl(var(--bc-ink-soft))] mt-1">
                        {receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference
                          ? "Signer, node timestamp and attestation reference returned by the KATLAS node."
                          : "KATLAS is unconfigured. The reference below is a BuildCierge local reference, not a KATLAS receipt hash."}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <KV
                      k={receipt.verification_status === "node_signed" && receipt.katlas_attestation_reference ? "KATLAS attestation reference" : "BuildCierge reference (local)"}
                      v={receipt.katlas_attestation_reference || receipt.receipt_hash}
                      mono
                    />
                    <KV k="Receipt type" v={receipt.receipt_type} />
                    <KV k="Event id" v={receipt.event_id} mono />
                    <KV k="Policy id" v={receipt.policy_id} />
                    <KV k="Actor" v={receipt.actor_role} />
                    <KV k="Counterparty" v={receipt.counterparty_role || "—"} />
                    <KV k="Outcome" v={receipt.outcome} />
                    <KV k="Verification" v={receipt.verification_status} />
                    <KV k="KATLAS status" v={receipt.katlas_status} />
                    <KV k="Withheld evidence" v={`${receipt.withheld_evidence_count} items`} />
                    {receipt.node_timestamp && <KV k="KATLAS node timestamp" v={receipt.node_timestamp} mono />}
                    {receipt.signer_reference && <KV k="KATLAS signer" v={receipt.signer_reference} />}
                  </div>
                  <div className="rule my-6" />
                  <p className="eyebrow mb-3">CAR summary</p>
                  <div className="space-y-3 text-sm">
                    <p><span className="eyebrow eyebrow-signal mr-2">Custody</span> {receipt.car_summary?.custody}</p>
                    <p><span className="eyebrow eyebrow-signal mr-2">Authority</span> {receipt.car_summary?.authority}</p>
                    <p><span className="eyebrow eyebrow-signal mr-2">Receipts</span> {receipt.car_summary?.receipts}</p>
                  </div>
                  <div className="mt-6">
                    <Link to={`/r/${receipt.receipt_hash}`} className="bc-btn-text" data-testid="open-public-receipt">Open public receipt →</Link>
                  </div>
                </>
              ) : (
                <p className="text-sm text-[hsl(var(--bc-muted))]">Record a governed action to register the first reference. Receipts only become KATLAS-signed once Stage 2 is active.</p>
              )}
            </Section>
          )}
        </div>

        {/* SIDEBAR: governed actions */}
        <aside className="lg:col-span-4 space-y-6">
          <Section testId="action-recorder" eyebrow="Record a governed action" title="">
            <form onSubmit={onRecord} className="space-y-3">
              <label className="block text-xs text-[hsl(var(--bc-muted))]">Action</label>
              <select
                className="w-full text-sm bg-[hsl(var(--bc-paper))] border border-[hsl(var(--border))] rounded p-2"
                value={actionState.action}
                onChange={(e) => {
                  const found = ACTIONS.find((a) => a.value === e.target.value);
                  setActionState({ ...actionState, action: e.target.value, actor_role: found?.role || actionState.actor_role });
                }}
                data-testid="action-select"
              >
                {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>

              <label className="block text-xs text-[hsl(var(--bc-muted))]">Actor role</label>
              <input
                className="w-full text-sm bg-[hsl(var(--bc-paper))] border border-[hsl(var(--border))] rounded p-2"
                value={actionState.actor_role}
                onChange={(e) => setActionState({ ...actionState, actor_role: e.target.value })}
                data-testid="action-actor"
              />

              <label className="block text-xs text-[hsl(var(--bc-muted))]">Note (optional)</label>
              <textarea
                rows={3}
                className="w-full text-sm bg-[hsl(var(--bc-paper))] border border-[hsl(var(--border))] rounded p-2"
                value={actionState.note}
                onChange={(e) => setActionState({ ...actionState, note: e.target.value })}
                data-testid="action-note"
              />

              <button type="submit" disabled={posting} className="bc-btn w-full justify-center" data-testid="action-submit">
                {posting ? "Recording…" : "Record governed action"}
              </button>
              <p className="text-[11px] text-[hsl(var(--bc-muted))]">
                Stage 1: the reference produced is a BuildCierge local reference, not a KATLAS receipt hash. No KATLAS signer, node timestamp or attestation reference is generated until Stage 2.
              </p>
            </form>
          </Section>

          <Section testId="action-history" eyebrow="Governed action history" title="">
            {actions.length === 0 && <p className="text-sm text-[hsl(var(--bc-muted))]">No governed actions yet.</p>}
            <ul className="space-y-3">
              {actions.slice().reverse().map((a) => (
                <li key={a.id} className="border-l-2 border-[hsl(var(--bc-signal))] pl-3">
                  <div className="text-[13px] font-medium">{a.action.replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-[hsl(var(--bc-muted))]">{a.actor_role} · <span className="font-mono">{a.receipt_hash?.slice(0, 12)}…</span></div>
                </li>
              ))}
            </ul>
          </Section>
        </aside>
      </section>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bc-card-flat p-4">
      <p className="eyebrow">{k}</p>
      <p className="font-display text-[28px] leading-none mt-2">{v}</p>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <p className="eyebrow mb-1">{k}</p>
      <p className={mono ? "font-mono text-[12px] break-all" : "text-sm"}>{v}</p>
    </div>
  );
}

function IntentCell({ k, v, mono, tone }) {
  return (
    <div className={`p-4 rounded border ${tone === "signal" ? "border-[hsl(var(--bc-signal)/0.35)] bg-[hsl(var(--bc-signal)/0.04)]" : "border-[hsl(var(--border))]"}`}>
      <p className="eyebrow mb-2">{k}</p>
      <p className={mono ? "font-mono text-[12px] break-all" : "text-[14px] leading-[1.55]"}>{v || "—"}</p>
    </div>
  );
}

function OutOfBandPanel({ oob }) {
  const confirmed = oob?.confirmation_status === "confirmed";
  return (
    <section
      className={`mt-8 bc-card p-7 ${confirmed ? "" : "border-[hsl(var(--bc-signal)/0.45)] bg-[hsl(var(--bc-signal)/0.05)]"}`}
      data-testid="out-of-band-panel"
    >
      <div className="flex items-start gap-3 mb-5">
        <MessagesSquare size={20} className="text-[hsl(var(--bc-signal))] mt-1" />
        <div>
          <p className="eyebrow eyebrow-signal mb-1" data-testid="out-of-band-eyebrow">Out-of-band decision risk</p>
          <h3 className="font-display text-[24px] leading-snug">
            {confirmed
              ? "Out-of-band decision brought back into the governed delivery record."
              : "A material decision happened outside the governed delivery record."}
          </h3>
          <p className="text-sm text-[hsl(var(--bc-ink-soft))] mt-2 max-w-3xl">
            BuildCierge does not monitor chat, email or meetings. It records the governed
            decision once an authorised role confirms what was decided, what evidence supports
            it and what context remains withheld.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
        <KV k="Communication source" v={COMM_SOURCE_LABEL[oob.communication_source_type] || oob.communication_source_type} />
        <KV k="Synthetic reference" v={oob.communication_reference} mono />
        <KV k="Authority holder" v={oob.authority_holder} />
        <KV k="Confirmation status" v={oob.confirmation_status} />
        <KV k="Affected scope" v={oob.affected_scope} />
        <KV k="Affected budget" v={oob.affected_budget} />
        <KV k="Affected release" v={oob.affected_release} />
        <KV k="Withheld context" v={`${oob.withheld_context_count} items (not exposed)`} />
      </div>

      <div className="rule my-6" />

      <p className="eyebrow mb-2">Decision summary (drafted, not yet authoritative)</p>
      <p className="text-[15px] leading-[1.6]" data-testid="oob-decision-summary">{oob.decision_summary}</p>

      <div className="rule my-6" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="eyebrow mb-2">Materiality reason</p>
          <p className="text-sm text-[hsl(var(--bc-ink-soft))]">{oob.materiality_reason}</p>
        </div>
        <div>
          <p className="eyebrow mb-2">Required action</p>
          <p className="text-sm text-[hsl(var(--bc-ink-soft))]">{oob.required_action}</p>
        </div>
      </div>

      {oob.evidence_refs?.length > 0 && (
        <>
          <div className="rule my-6" />
          <p className="eyebrow mb-3">Supporting evidence references (synthetic — no raw content)</p>
          <ul className="space-y-2 text-sm">
            {oob.evidence_refs.map((e, i) => (
              <li key={i} className="flex gap-2"><span className="signal-dot mt-2" />{e}</li>
            ))}
          </ul>
        </>
      )}

      <div className="rule my-6" />
      <p className="text-xs text-[hsl(var(--bc-muted))]" data-testid="oob-boundary-note">
        {oob.boundary_note ||
          "BuildCierge does not ingest raw messages, transcripts, screenshots or personal identifiers."}
      </p>
    </section>
  );
}
